import { CouponPendingParams } from '@shared/api/couponSettlement/couponSettlement.controller';
import {
  CouponPendingRowBase,
  CouponSettleSkipReport,
} from '@shared/api/couponSettlement/couponSettlement.interface';
import { LowerRole } from '@shared/types';

import { getKstDateParts, toYmd } from './rebate-settlement.utils';

/** 상조 전용 쿠폰 정산 API 역할 */
export const ROLE = LowerRole.BugoAgencyAdmin;

/**
 * 쿠폰 ID 열거 요청(settle·patch add*)의 한 번당 상한 — 백엔드 body 한도(100KB) 근거.
 * ObjectId 1개 ≈ 27B, 3,000개 ≈ 81KB. 집행(CouponPendingSection)과 쿠폰 추가 피커가 공유.
 */
export const MAX_ENUM_IDS = 3000;

/** 숫자 표기 (없으면 '-') — 정산 테이블들의 공통 셀 포매터 */
export const won = (n?: number): string => (n != null ? n.toLocaleString('ko-KR') : '-');

/** 집행/라인편집 반려(skipped) 사유 → 운영자 문구 (settle·PATCH 응답 공용) */
export const SKIP_REASON_LABELS: Record<CouponSettleSkipReport['reason'], string> = {
  notFound: '쿠폰 없음',
  brandMismatch: '상조 불일치',
  alreadySettled: '다른 정산에 이미 포함',
  notDelivered: '미발송',
  excluded: '재발행/연장 제외',
  noRefund: '환불 정보 없음',
  canceled: '취소된 쿠폰',
  overQuota: '구매수량 초과',
};

/** 반려 목록 요약 문구 — "N건 반려 (사유1, 사유2)". 알 수 없는 사유는 원문 유지. */
export const describeSkipped = (skipped: CouponSettleSkipReport[]): string => {
  if (!skipped.length) return '';
  const reasons = Array.from(
    new Set(skipped.map((s) => SKIP_REASON_LABELS[s.reason] ?? s.reason)),
  );
  return `${skipped.length}건 반려 (${reasons.join(', ')})`;
};

/**
 * 정산금 표기. 음수는 상조 상계금이므로 절댓값 앞에 ▲를 붙인다(에러 아님).
 * @example formatSettlementAmount(130640) // '130,640원'
 * @example formatSettlementAmount(-8060)  // '▲8,060원'
 */
export const formatSettlementAmount = (n: number): string =>
  `${n < 0 ? '▲' : ''}${Math.abs(n).toLocaleString('ko-KR')}원`;

/** 정산금 색: 음수(회수)는 rose, 그 외 gray */
export const amountColorClass = (n: number): string =>
  n < 0 ? 'text-rose-700' : 'text-gray-800';

/** 정산금 라벨: 음수는 회수 예정, 그 외 지급 예정 */
export const amountLabel = (n: number): string => (n < 0 ? '회수 예정' : '지급 예정');

/**
 * 활동월(year, month)을 그 달 1일~말일의 `YYYY-MM-DD` 범위로 변환한다.
 * 기간 빠른선택(이번달/지난달) 버튼이 date input 값을 세팅할 때 쓴다.
 * 월말일은 `new Date(year, month, 0)`로 구한다(다음달 0일 = 이번달 말일).
 */
export const monthYmdRange = (
  year: number,
  month: number,
): { from: string; to: string } => ({
  from: toYmd(new Date(year, month - 1, 1)),
  to: toYmd(new Date(year, month, 0)),
});

/**
 * date input의 `YYYY-MM-DD` from/to를 pending 조회 파라미터로 변환한다.
 * KST(+09:00) 기준 from은 00:00:00, to는 23:59:59. 빈 문자열 경계는 생략한다
 * (둘 다 비면 빈 객체 → 백엔드가 미정산 전체 반환).
 */
export const ymdRangeToIso = (from: string, to: string): CouponPendingParams => {
  const params: CouponPendingParams = {};
  if (from) params.from = `${from}T00:00:00+09:00`;
  if (to) params.to = `${to}T23:59:59+09:00`;
  return params;
};

/** KST 기준 지난달 (1월이면 전년 12월) */
export const lastMonthKst = (): { year: number; month: number } => {
  const { year, month } = getKstDateParts();
  return month === 1 ? { year: year - 1, month: 12 } : { year, month: month - 1 };
};

/**
 * 수수료/소수 금액 표기 — 부동소수 잡음만 제거(참값은 보존).
 * 수수료·정산금의 계산 자체는 단계별 로드 전환 이후 전부 서버(export/detail/summary
 * 응답의 fee·totalFee·amount)에서 오고, 프론트엔 이 표기 함수만 남는다.
 */
export const formatFee = (n: number): string =>
  n.toLocaleString('ko-KR', { maximumFractionDigits: 6 });

/**
 * 쿠폰코드 표시 — 서버 fullCode('코드-순번')에서 `-순번` 꼬리를 떼고 코드만 보여준다.
 * @example couponCodeLabel('AB12CD-3') // 'AB12CD'
 */
export const couponCodeLabel = (fullCode?: string): string =>
  fullCode ? fullCode.replace(/-\d+$/, '') : '-';

/**
 * 행별 검색 대상 문자열 캐시 — 수천 행 목록에서 키 입력마다 필드 12개를
 * 소문자화/숫자 추출하는 비용을 행 생애 1회로 줄인다 (행 객체 기준 WeakMap).
 * text는 줄바꿈, digits는 쉼표로 구분해 필드 간 경계가 검색에 섞이지 않게 한다.
 */
const rowSearchBlobCache = new WeakMap<
  CouponPendingRowBase,
  { text: string; digits: string }
>();

const rowSearchBlob = (r: CouponPendingRowBase): { text: string; digits: string } => {
  const cached = rowSearchBlobCache.get(r);
  if (cached) return cached;
  const blob = {
    text: [
      r.fullCode,
      couponCodeLabel(r.fullCode),
      r.receiverName,
      r.buyerName,
      r.purchaseItemName,
      r.receiveItemName,
      r.deceasedName,
      r.workerName,
      r.teamName,
    ]
      .map((v) => (v ?? '').toLowerCase())
      .join('\n'),
    digits: [r.receiverPhoneNumber, r.buyerPhoneNumber, r.workerPhoneNumber]
      .map((v) => (v ?? '').replace(/\D/g, ''))
      .join(','),
  };
  rowSearchBlobCache.set(r, blob);
  return blob;
};

/**
 * 미정산/정산 행 통합 검색 매칭 — 쿠폰번호·수령자·구매자명·상품·고인·지도사는 텍스트로,
 * 연락처(수령자/구매자/지도사)는 숫자만 뽑아 부분일치로 본다(하이픈 유무 무관).
 * 미정산 상세 모달·정산 상세 모달·쿠폰 추가 피커가 공유한다.
 */
export const matchPendingRow = (r: CouponPendingRowBase, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const blob = rowSearchBlob(r);
  if (blob.text.includes(q)) return true;
  const qDigits = q.replace(/\D/g, '');
  if (qDigits.length < 3) return false;
  return blob.digits.includes(qDigits);
};
